import { Ability, defineAbility } from '@casl/ability'
import { createContext, useMemo } from 'react'
import type { UserRole } from './types'

// Define subjects that need organization checks
type Subjects = 'Recreation' | 'all' | string

export const useAbilityRoleSchema = (
    role: string[],
    roleSchema: Record<string, unknown>,
    _userRoles: UserRole[],
    organizationIds?: string[]
) => {
    return useMemo(() => {
        return defineAbility<
            Ability<[string, Subjects | { organizationId?: string }]>
        >((can, cannot) => {
            const schema = role.map((item) => {
                const schemaItem = roleSchema[item]

                if (Array.isArray(schemaItem)) {
                    return schemaItem
                } else if (
                    typeof schemaItem === 'object' &&
                    schemaItem !== null
                ) {
                    return Object.entries(schemaItem)
                }
                return []
            })

            schema?.forEach((item) => {
                if (Array.isArray(item)) {
                    item.forEach(([action, subject]) => {
                        // Super admin can do anything
                        if (role.includes('super-admin')) {
                            can(action, subject)
                            return
                        }
                        // For recreation-related actions by recreation admins
                        if (
                            subject === 'Recreation' &&
                            (role.includes('recreation-admin') ||
                                role.includes('recreation-trainer'))
                        ) {
                            if (organizationIds && organizationIds.length > 0) {
                                // Only allow actions on recreations from their organization(s)
                                organizationIds.forEach((orgId) => {
                                    can(action, subject, {
                                        organizationId: orgId,
                                    })
                                })
                            } else {
                                // If no organization IDs, deny access
                                cannot(action, subject)
                            }
                        } else {
                            // For all other roles and subjects, use default behavior
                            can(action, subject)
                        }
                    })
                }
            })
        })
    }, [role, roleSchema, organizationIds])
}
export const AbilityContext = createContext({
    ability: defineAbility(() => {}),
    organizationIds: [] as string[] | undefined,
    role: [] as string[],
    userRoles: [] as UserRole[],
    userId: null as string | null | undefined,
})
